import * as React from "react";

import { Button, Group, Modal, ScrollArea, Select, Stack, Title } from "@mantine/core";
import { StyleSheet } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { AddonTemplate, isAircraftAddonTemplate } from "../../lib/model/addonTemplate";
import { ChecklistContents } from "../../lib/model/checklistAppState";
import { SerializedChecklistContents } from "../../lib/model/checklistSerialized";
import { DocumentLayout, documentLayoutNames, documentLayouts } from "../../lib/model/layout";
import { appStateFromSerialized } from "../../lib/state/convert";
import { defaultDocumentStyles } from "../../lib/state/defaultStyles";
import { librarySlice } from "../../lib/state/reducer";
import { SoftPDFPreview } from "../renderer/softpdf/SoftPDFPreview";

export const AddonPreviewModal = ({
  template,
  open,
  onClose,
}: {
  template: AddonTemplate | null;
  open: boolean;
  onClose: () => void;
}): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [layout, setLayout] = React.useState<DocumentLayout>("twoColumn");

  if (template) {
    const onSelect = () => {
      if (isAircraftAddonTemplate(template)) {
        reduxDispatch(
          librarySlice.actions.newDocumentFromTemplate({
            baseTemplate: template,
            softwareAddons: [],
          })
        );
        navigate("/editor");
      }
    };

    const previewSerialized: SerializedChecklistContents = {
      title: template.title,
      checklists: template.content,
    };
    const previewDocument: ChecklistContents = appStateFromSerialized(previewSerialized);
    const previewStyleSheet = StyleSheet.create(defaultDocumentStyles);

    return (
      <Modal
        opened={open}
        title={<Title order={3}>Preview: {template.title}</Title>}
        onClose={onClose}
        size="70%"
      >
        <Stack spacing="xs">
          <ScrollArea
            style={{ height: "70vh", border: "1px solid #000" }}
            p="lg"
            styles={(theme) => ({
              root: {
                backgroundColor:
                  theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[4],
              },
            })}
          >
            <SoftPDFPreview
              checklistDocumentSnapshot={previewDocument}
              stylesheetSnapshot={previewStyleSheet}
              layout={layout}
            />
          </ScrollArea>
          <Stack align="center">
            <Select
              label="Preview Layout"
              value={layout}
              onChange={(newLayout) =>
                (newLayout === "oneColumn" || newLayout === "twoColumn") && setLayout(newLayout)
              }
              data={documentLayouts.map((layout) => ({
                value: layout,
                label: documentLayoutNames[layout],
              }))}
              style={{ width: "20ch" }}
            />
            <Group position="center">
              <Button onClick={onSelect} variant="gradient">
                Select Template
              </Button>
              <Button onClick={onClose} variant="outline">
                Close
              </Button>
            </Group>
          </Stack>
        </Stack>
      </Modal>
    );
  } else {
    return <></>;
  }
};
